import { Play, Clock, Star } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import type { DemoVideo } from '@/constants/demoVideos';

interface DemoVideoCardProps {
  video: DemoVideo;
  onPlay: (video: DemoVideo) => void;
}

export function DemoVideoCard({ video, onPlay }: DemoVideoCardProps) {
  return (
    <Card
      className="group cursor-pointer overflow-hidden transition-all hover:shadow-lg hover:scale-[1.02]"
      onClick={() => onPlay(video)}
    >
      <div className="relative aspect-video bg-gradient-to-br from-primary/20 to-primary/5">
        {/* Play button overlay */}
        <div className="absolute inset-0 flex items-center justify-center bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity">
          <div className="w-16 h-16 rounded-full bg-primary/90 flex items-center justify-center shadow-lg">
            <Play className="h-8 w-8 text-white ml-1" fill="white" />
          </div>
        </div>

        {/* Video icon placeholder */}
        <div className="absolute inset-0 flex items-center justify-center group-hover:opacity-0 transition-opacity">
          <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
            <Play className="h-6 w-6 text-primary ml-0.5" />
          </div>
        </div>

        {/* Duration badge */}
        <div className="absolute bottom-2 right-2">
          <Badge variant="secondary" className="bg-black/70 text-white text-xs">
            <Clock className="h-3 w-3 mr-1" />
            {video.duration}
          </Badge>
        </div>

        {/* Featured badge */}
        {video.isFeatured && (
          <div className="absolute top-2 left-2">
            <Badge className="bg-primary text-white text-xs">
              <Star className="h-3 w-3 mr-1" fill="currentColor" />
              Featured
            </Badge>
          </div>
        )}
      </div>

      <CardContent className="p-4">
        <h3 className="font-semibold text-sm mb-1 line-clamp-1 group-hover:text-primary transition-colors">
          {video.title}
        </h3>
        <p className="text-xs text-muted-foreground line-clamp-2">
          {video.description}
        </p>
      </CardContent>
    </Card>
  );
}
